import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Printer, Settings, Trash2, Eye, FileText, Building2, GitBranch } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Dropdown } from '../../components/ui/Dropdown';
import { DataTable } from '../../components/ui/DataTable';
import { ReportConfigModal } from '../../components/reports/ReportConfigModal';
import { DocumentPreview } from '../../components/reports/preview/DocumentPreview';
import { ReportHeader } from '../../components/reports/ReportHeader';
import { PrintableReport } from '../../components/reports/PrintableReport';
import { useReports } from '../../hooks/useReports';
import { useSavedReports } from '../../hooks/useSavedReports';
import { useFinancialExport } from '../../hooks/useFinancialExport';
import { useOrganization } from '../../hooks/useOrganization';
import { useDashboard } from '../../hooks/useDashboard';
import { useBusinessUnitStore } from '../../stores/businessUnitStore';
import { useEntityVocabulary } from '../../hooks/useEntityVocabulary';
import { useAuth0 } from '@auth0/auth0-react';
import type { FinancialStatementType } from '../../types/reports';
import type { GeneratedReport } from '../../services/storage/ReportsStorageService';

const STATEMENT_TYPES: Record<FinancialStatementType, string> = {
  'balance': 'Bilan',
  'income': 'Compte de Résultat',
  'cashflow': 'Tableau des Flux de Trésorerie',
  'equity-changes': 'Tableau de Variation des Capitaux Propres',
  'notes': 'Notes Annexes',
  'reconciliation': 'État de Rapprochement',
  'analytical': 'État Analytique',
  'social': 'Bilan Social',
  'statistics': 'État Statistique',
  'trial-balance': 'Balance Générale',
  'general-ledger': 'Grand Livre',
  'journal-book': 'Livre Journal',
  'aged-receivables': 'Balance Âgée Clients',
  'aged-payables': 'Balance Âgée Fournisseurs',
};

export function ReportsPage() {
  const { user } = useAuth0();
  const { organization: orgData } = useOrganization();
  const { dashboardData, loading: dashboardLoading } = useDashboard();
  // Vocabulaire dynamique : "Succursale" (PME) ↔ "Coopérant" (coopérative).
  const vocab = useEntityVocabulary();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<GeneratedReport | null>(null);
  const [currentConfig, setCurrentConfig] = useState<{
    type: FinancialStatementType;
    format: 'pdf' | 'excel';
    currency: 'USD' | 'CDF';
  }>({
    type: 'balance',
    format: 'pdf',
    currency: 'CDF'
  });

  const { data, metadata, loading, error, generateReport } = useReports();
  const { savedReports, loading: savedLoading, saveReport, deleteReport } = useSavedReports();
  const { loading: exportLoading, handleExport } = useFinancialExport();
  const { selectedBusinessUnit, effectiveFilter, hierarchy, includeChildren } = useBusinessUnitStore();
  
  // Informations de l'entreprise: utiliser les métadonnées du backend si disponibles, sinon fallback
  const organization = metadata?.company ? {
    name: metadata.company.name,
    address: '', // Non fourni par le backend
    registrationNumber: metadata.company.rccm || '',
    taxId: metadata.company.nif || '',
    idnat: metadata.company.idnat || '',
    creditScore: metadata.company.creditRating ? parseInt(metadata.company.creditRating) || null : (dashboardData?.keyPerformanceIndicators?.creditScore ?? null),
    esgScore: metadata.company.esgCode || 'N/A',
    financialRating: metadata.company.financialScore || (dashboardData?.keyPerformanceIndicators?.financialRating ?? 'N/A'),
    logo: metadata.company.logo,
    audited: metadata.company.audited,
    auditorCode: metadata.company.auditorCode,
    generatedBy: metadata.company.generatedBy,
    generatedDate: metadata.company.generatedDate
  } : orgData ? {
    name: orgData.name,
    address: [orgData.address, orgData.city, orgData.country].filter(Boolean).join(', '),
    registrationNumber: orgData.registrationNumber,
    taxId: orgData.taxId,
    creditScore: dashboardData?.keyPerformanceIndicators?.creditScore ?? null,
    esgScore: 'N/A', // ESG non disponible dans l'API
    financialRating: dashboardData?.keyPerformanceIndicators?.financialRating ?? 'N/A',
    logo: orgData.logo,
    industry: orgData.industry,
    legalForm: orgData.legalForm,
    phone: orgData.phone,
    email: orgData.email,
    website: orgData.website
  } : {
    name: 'Chargement...',
    address: '',
    registrationNumber: '',
    taxId: '',
    creditScore: dashboardData?.keyPerformanceIndicators?.creditScore ?? null,
    esgScore: 'N/A',
    financialRating: dashboardData?.keyPerformanceIndicators?.financialRating ?? 'N/A'
  };

  // Référence pour éviter les sauvegardes multiples du même rapport
  const lastSavedDataRef = useRef<any>(null);

  // Sauvegarder automatiquement le rapport généré et ouvrir le modal
  useEffect(() => {
    // Vérifier qu'on a des données, un utilisateur, et qu'on n'est pas en chargement
    if (!data || !user || loading) {
      return;
    }
    
    // Éviter de sauvegarder le même rapport plusieurs fois
    if (lastSavedDataRef.current === data) {
      return;
    }
    
    // Marquer ce rapport comme sauvegardé
    lastSavedDataRef.current = data;
    
    const autoSaveReport = async () => {
      try {
        // Récupérer le nom de l'utilisateur depuis Auth0 ou utiliser l'email
        const generatedBy = user.name || user.email || user.nickname || 'Utilisateur';
        
        // Récupérer les infos de l'organisation
        const orgName = organization?.name || metadata?.company?.name || 'Organisation';
        const orgRegNumber = organization?.registrationNumber || metadata?.company?.rccm || '';
        
        const savedReport = await saveReport({
          type: currentConfig.type,
          title: STATEMENT_TYPES[currentConfig.type],
          data,
          currency: currentConfig.currency,
          generatedBy,
          organization: {
            name: orgName,
            registrationNumber: orgRegNumber
          },
          isAudited: metadata?.company?.audited ?? false,
          format: 'json'
        });
        
        // Ouvrir le modal d'aperçu pour le rapport nouvellement généré
        if (savedReport) {
          setSelectedReport(savedReport);
          setShowPreviewModal(true);
        }
      } catch (err) {
        console.error('❌ Error saving report:', err);
        // Réinitialiser la ref pour permettre une nouvelle tentative
        lastSavedDataRef.current = null;
      }
    };
    
    autoSaveReport();
  }, [data, loading, user, currentConfig.type, currentConfig.currency, organization, metadata, saveReport]);

  const handleConfigSubmit = async (config: any) => {
    setCurrentConfig({
      type: config.type,
      format: config.format,
      currency: config.currency
    });
    
    // Construire les paramètres selon le type de rapport
    const reportParams: any = {
      type: config.type,
      format: config.format,
      comparative: config.showComparison,
      currency: config.currency,
      includeNotes: config.showAnalytics,
      template: config.template,
    };

    // Rapports à date unique : bilan, notes, balance générale
    const singleDateTypes = ['balance', 'notes', 'trial-balance'];
    if (singleDateTypes.includes(config.type)) {
      reportParams.date = config.date;
      // Paramètres spécifiques à la balance générale
      if (config.type === 'trial-balance') {
        reportParams.includeOpeningBalance = config.includeOpeningBalance ?? true;
        reportParams.openingDate = config.openingDate;
        reportParams.groupByClass = config.groupByClass ?? true;
      }
    } else {
      // income, cashflow, equity-changes utilisent une période
      reportParams.startDate = config.startDate;
      reportParams.endDate = config.endDate;
    }

    await generateReport(reportParams);

    setShowConfigModal(false);
  };

  const handleViewReport = (report: GeneratedReport) => {
    setSelectedReport(report);
    setCurrentConfig({
      type: report.type,
      format: 'pdf',
      currency: report.currency
    });
    setShowPreviewModal(true);
  };

  const handleDeleteReport = async (reportId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      const success = await deleteReport(reportId);
      if (success && selectedReport?.id === reportId) {
        setSelectedReport(null);
        setShowPreviewModal(false);
      }
    }
  };

  const handlePrintReport = (report: GeneratedReport) => {
    // Préparer le rapport pour l'impression
    setSelectedReport(report);
    setCurrentConfig({
      type: report.type,
      format: 'pdf',
      currency: report.currency
    });
    
    // Attendre que le state soit mis à jour puis imprimer directement
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const handleExportClick = async (format: 'pdf' | 'excel') => {
    if (!data || !user) return;
    
    try {
      await handleExport({
        type: currentConfig.type,
        format,
        data,
        title: STATEMENT_TYPES[currentConfig.type],
        organization,
        generatedBy: user.name || user.email || 'N/A',
        isAudited: selectedReport?.isAudited ?? organization?.audited ?? false,
        currency: currentConfig.currency
      });
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };  const handlePrint = () => {
    // Référence à l'iframe qui sera utilisée pour l'impression
    const printFrame = document.getElementById('report-print-frame') as HTMLIFrameElement;
    if (!printFrame || !printFrame.contentWindow) return;

    // Accéder au document de l'iframe
    const printDocument = printFrame.contentWindow.document;
    
    // Ouvrir le document pour écriture
    printDocument.open();
    
    // Écrire le contenu HTML d'impression dans l'iframe
    printDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${STATEMENT_TYPES[currentConfig.type]} - Impression</title>
          <meta charset="utf-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #000;
              background: #fff;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
            }
            th {
              background-color: #f8f9fa;
              font-weight: bold;
              text-align: left;
            }
            .text-right {
              text-align: right;
            }
            .bg-gray-700 {
              background-color: #374151;
              color: white;
            }
            .bg-gray-50 {
              background-color: #f9fafb;
            }
            .bg-gray-100 {
              background-color: #f3f4f6;
            }
            .font-bold {
              font-weight: bold;
            }
            .font-medium {
              font-weight: 500;
            }
            h1, h2, h3 {
              color: #111827;
            }
            .text-center {
              text-align: center;
            }
            .mb-8 {
              margin-bottom: 32px;
            }
            .mb-4 {
              margin-bottom: 16px;
            }
            .mb-2 {
              margin-bottom: 8px;
            }
            .mb-1 {
              margin-bottom: 4px;
            }
            .mt-8 {
              margin-top: 32px;
            }
            .mt-4 {
              margin-top: 16px;
            }
            .border-t {
              border-top: 1px solid #e5e7eb;
            }
            .pt-4 {
              padding-top: 16px;
            }
            .text-gray-600 {
              color: #4b5563;
            }
            .text-sm {
              font-size: 0.875rem;
            }
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div id="print-content"></div>
        </body>
      </html>
    `);
    
    // Fermer le document
    printDocument.close();
    
    // Rendre notre composant React dans l'élément print-content de l'iframe
    if (data && user) {
      const printContainer = printDocument.getElementById('print-content');
      if (printContainer && printFrame.contentWindow) {
        // Injecter le contenu HTML du rapport
        printContainer.innerHTML = document.getElementById('printable-report-content')?.innerHTML || '';
        
        // Attendre que le contenu soit complètement chargé
        setTimeout(() => {
          printFrame.contentWindow?.print();
        }, 500);
      }
    }
  };

  // Données à afficher (rapport sélectionné ou rapport actuel)
  const displayData = selectedReport?.data || data;
  const displayType = selectedReport?.type || currentConfig.type;
  const displayCurrency = selectedReport?.currency || currentConfig.currency;

  // Couleurs des badges selon le type de rapport
  const getReportBadgeVariant = (type: FinancialStatementType): 'primary' | 'success' | 'info' | 'warning' => {
    const variants: Record<string, 'primary' | 'success' | 'info' | 'warning'> = {
      'balance':        'primary',
      'income':         'success',
      'cashflow':       'info',
      'equity-changes': 'warning',
      'notes':          'info',
      'reconciliation': 'primary',
      'analytical':     'warning',
      'social':         'success',
      'statistics':     'info',
      'trial-balance':  'warning',
      'general-ledger': 'primary',
      'journal-book':   'info',
    };
    return variants[type] || 'primary';
  };

  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">États Financiers</h1>
        <Button 
          variant="primary" 
          icon={Settings}
          onClick={() => setShowConfigModal(true)}
        >
          Générer un nouveau rapport
        </Button>
      </div>

      {/* Indicateur BU / Périmètre de consolidation */}
      {selectedBusinessUnit && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Périmètre :
              </span>
              <Badge variant="primary">{selectedBusinessUnit.name}</Badge>
              <Badge variant="default" className="text-xs">
                {selectedBusinessUnit.type === 'company'
                  ? vocab.entityCapitalized
                  : selectedBusinessUnit.type === 'branch'
                    ? vocab.branchCapitalized
                    : 'Point de vente'}
              </Badge>
            </div>
            {includeChildren && effectiveFilter.businessUnitIds && effectiveFilter.businessUnitIds.length > 1 && (
              <div className="flex items-center gap-2">
                <GitBranch className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  Consolidation hiérarchique ({effectiveFilter.businessUnitIds.length} unités)
                </span>
              </div>
            )}
            {!includeChildren && (
              <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                Données isolées (sans enfants)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tableau des rapports sauvegardés */}
      <Card title={`Rapports générés (${savedReports.length})`}>
        <DataTable
          columns={[
            {
              header: 'Type de rapport',
              accessor: (report) => (
                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-primary" />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{STATEMENT_TYPES[report.type]}</span>
                    <Badge variant={getReportBadgeVariant(report.type)} className="mt-1 w-fit">
                      {report.type}
                    </Badge>
                  </div>
                </div>
              ),
              sortable: true,
              width: 200
            },
            {
              header: 'Date de génération',
              accessor: (report) => new Date(report.generatedAt).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }),
              sortable: true
            },
            {
              header: 'Devise',
              accessor: (report) => (
                <Badge variant={report.currency === 'USD' ? 'success' : 'primary'}>
                  {report.currency}
                </Badge>
              ),
              className: 'text-center',
              sortable: true
            },
            {
              header: 'Généré par',
              accessor: 'generatedBy',
              sortable: true,
              filterable: true
            },
            {
              header: 'Organisation',
              accessor: (report) => (
                <div className="flex flex-col">
                  <span className="text-sm">{report.organization.name}</span>
                  {report.isAudited && (
                    <Badge variant="success" className="mt-1 w-fit text-xs">
                      Audité
                    </Badge>
                  )}
                </div>
              ),
              sortable: true
            },
            {
              header: 'Actions',
              accessor: (report) => (
                <Dropdown
                  items={[
                    {
                      label: 'Voir',
                      icon: Eye,
                      onClick: () => handleViewReport(report),
                      variant: 'primary'
                    },
                    {
                      label: 'Imprimer',
                      icon: Printer,
                      onClick: () => handlePrintReport(report),
                      separator: true
                    },
                    {
                      label: 'Exporter Excel',
                      icon: Download,
                      onClick: async () => {
                        if (!report.data || !user) return;
                        
                        try {
                          await handleExport({
                            type: report.type,
                            format: 'excel',
                            data: report.data,
                            title: STATEMENT_TYPES[report.type],
                            organization,
                            generatedBy: report.generatedBy,
                            isAudited: report.isAudited,
                            currency: report.currency
                          });
                        } catch (error) {
                          console.error('Failed to export:', error);
                          alert('Erreur lors de l\'exportation du rapport');
                        }
                      },
                      variant: 'success'
                    },
                    {
                      label: 'Supprimer',
                      icon: Trash2,
                      onClick: () => handleDeleteReport(report.id),
                      variant: 'danger',
                      separator: true
                    }
                  ]}
                  align="right"
                />
              ),
              className: 'text-center',
              width: 80,
              sortable: false,
              hideable: false
            },
          ]}
          data={savedReports}
          loading={savedLoading}
          emptyTitle="Aucun rapport généré pour le moment"
          emptyAction={{
            label: 'Générer un rapport',
            onClick: () => setShowConfigModal(true),
            icon: 'plus'
          }}
          showToolbar={true}
          enableResize={true}
          enableSort={true}
          enableFilters={true}
          enableColumnToggle={true}
          enableGlobalFilter={true}
          showPagination={true}
          storageKey="reports-page-table"
          density="normal"
        />
      </Card>

      {/* Modal d'aperçu du rapport */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={displayData ? STATEMENT_TYPES[displayType] : "Aperçu du rapport"}
        size="2xl"
      >
        {displayData && (
          <div className="space-y-6">
            {/* En-tête du rapport */}
            {user && (
              <ReportHeader
                title={STATEMENT_TYPES[displayType]}
                organization={organization}
                generatedBy={selectedReport?.generatedBy || organization?.generatedBy || user.name || user.email || 'N/A'}
                isAudited={selectedReport?.isAudited ?? organization?.audited ?? false}
                currency={displayCurrency}
                fiscalYear={metadata?.fiscalYear}
                generatedDate={metadata?.generatedDate || organization?.generatedDate}
              />
            )}

            {/* Prévisualisation */}
            <DocumentPreview 
              format={currentConfig.format}
              data={displayData}
              type={displayType}
              currency={displayCurrency as any}
              loading={loading}
            />

            {/* Message d'erreur */}
            {error && (
              <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                {error}
              </div>
            )}

            {/* Boutons d'action dans le modal */}
            <div className="flex justify-end space-x-3 border-t pt-4 mt-6">
              <Button 
                variant="secondary" 
                icon={Printer}
                onClick={handlePrint}
              >
                Imprimer
              </Button>
              <Button 
                variant="primary" 
                icon={Download}
                onClick={() => handleExportClick('excel')}
                disabled={exportLoading}
              >
                Exporter Excel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de configuration */}
      <ReportConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSubmit={handleConfigSubmit}
      />

      {/* Zone d'impression cachée */}
      <div className="hidden">
        <div id="printable-report-content">
          {displayData && user && (
            <PrintableReport
              data={displayData}
              type={displayType}
              title={STATEMENT_TYPES[displayType]}
              organization={organization}
              generatedBy={selectedReport?.generatedBy || user.name || user.email || 'N/A'}
              currency={displayCurrency}
              date={new Date().getFullYear().toString()}
              isAudited={selectedReport?.isAudited ?? organization?.audited ?? false}
            />
          )}
        </div>
        <iframe id="report-print-frame" style={{ display: 'none' }}></iframe>
      </div>
    </div>
  );
}